import type { ReactNode } from 'react';

/**
 * Single source of truth for every info-icon tooltip on the portal.
 * The target audience is you three months from now trying to remember
 * what each number means. Keep copy short (2-3 sentences), name the
 * computation when relevant ("max(0, Locked − Paid + Adjustments)"
 * beats "what you're owed"), and prefer facts to vibes.
 *
 * When adding a new HintKey:
 *   1. Add it to the union type below.
 *   2. Add the matching entry to `infoHints`.
 *   3. Reference it with <InfoHint hintKey="your-key" />.
 */

export type HintKey =
  | 'lockedValue'
  | 'amountPaid'
  | 'netOwed'
  | 'bracketBonus'
  | 'nichesBonus'
  | 'payoutStatus'
  | 'payoutTotalAmount'
  | 'payoutWeekOf'
  | 'adminAdjustments'
  | 'adminRetainer'
  | 'adminPaypal'
  | 'owedBreakdownExplainer';

interface Hint {
  label: string;
  detail: ReactNode;
}

export const infoHints: Record<HintKey, Hint> = {
  lockedValue: {
    label: 'Locked value',
    detail:
      'What this invite is worth at conversion. Composed of the base log/join/post bonuses, niche bonuses, and the follower-bracket crossing. Snapshotted when the creator joins — later rate edits do not retroactively apply (SPA-1207).',
  },
  amountPaid: {
    label: 'Paid',
    detail:
      'Sum of cash ledger events (PAYOUT, RECONCILE, REVERSAL). Changes only when real money moves. The guard trigger on amount_paid refuses any direct write — every change routes through paymentLedger.emit.',
  },
  netOwed: {
    label: 'Owed',
    detail:
      'max(0, Locked − Paid + Adjustments). Zero can mean paid in full, rejected as duplicate, or offset by an admin adjustment. Expand "Why is this $0?" below for the chronological ledger.',
  },
  bracketBonus: {
    label: 'Bracket bonus',
    detail:
      'Extra attributed when the creator\'s follower count crosses a bracket threshold at conversion time. SPA-1207 pins this to the highest follower count ever observed per invite, so brackets don\'t get clawed back when a creator loses followers.',
  },
  nichesBonus: {
    label: 'Niche bonus',
    detail:
      'Niches boosted this cycle pay extra on top of the base rate. Multiple niches stack additively up to the per-invite cap configured in the rate card.',
  },
  payoutStatus: {
    label: 'Payout status',
    detail:
      'PENDING → queued · SUBMITTED → sent to provider · PAID → money landed · FAILED / CANCELLED → ask an admin. SUBMITTED clipping payouts cannot be cancelled (clipping has no cancel API).',
  },
  payoutTotalAmount: {
    label: 'Total amount',
    detail:
      'Sum of this payout\'s per-invite line items plus any admin additionalAmount. Each line credits the matching invite via a PAYOUT event when the payout settles.',
  },
  payoutWeekOf: {
    label: 'Week of',
    detail: 'Monday of the payroll cycle this payout covers (UTC).',
  },
  adminAdjustments: {
    label: 'Adjustments',
    detail:
      'Signed deltas against an inviter\'s owed total. REJECT events (duplicate guard or admin reject) go negative; ADJUSTMENT events can go either way. Sum is read from invite_owed_v (SPA-1208), the one source of truth.',
  },
  adminRetainer: {
    label: 'Retainer',
    detail:
      'Weekly flat due for RETAINER-model inviters. Per-conversion bounties are NOT added on top — the retainer is the full compensation for the cycle.',
  },
  adminPaypal: {
    label: 'PayPal',
    detail: 'Routing target for payroll. Missing = Pay button disabled.',
  },
  owedBreakdownExplainer: {
    label: 'Why this number',
    detail:
      'Chronological ledger of every event that moved this invite\'s owed total. REJECT and ADJUSTMENT events zero the number without touching Paid; PAYOUT and RECONCILE events move Paid directly.',
  },
};
